import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRP Registration Guide | Form 2290 & Apportioned Plates"
          description="Learn how IRP registration works, who needs it, required documents, and how it connects with IRS Form 2290. Stay compliant and register with ease today."
          keywords="form 2290 instructions, form 2290, 2290 online, heavy vehicle use tax, irs form 2290, 2290 tax, form 2290 online"
          canonical="resources/excise-tax/irp/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          International Registration Plan{' '}
          <span className="highlight-container">
            <span className="highlight"> (IRP)</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Simplify Vehicle Registration across the state into a single
          Registration!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=irp`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/irp/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        International Registration Plan (IRP) and Form 2290 at a
                        Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>IRP:</strong>{' '}
                            The International Registration Plan allows
                            commercial vehicles to operate across multiple
                            states and provinces with{' '}
                            <strong style={{ fontWeight: 600 }}>
                              apportioned registration.
                            </strong>
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Form 2290:
                            </strong>{' '}
                            Used to report and pay the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              federal Heavy Vehicle Use Tax (HVUT)
                            </strong>{' '}
                            on vehicles with a taxable gross weight of{' '}
                            <strong style={{ fontWeight: 600 }}>
                              55,000 pounds or more.
                            </strong>
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Key Difference:
                            </strong>{' '}
                            IRP handles{' '}
                            <strong style={{ fontWeight: 600 }}>
                              vehicle registration and apportioned fees,
                            </strong>{' '}
                            while Form 2290 handles{' '}
                            <strong style={{ fontWeight: 600 }}>
                              federal highway use tax.
                            </strong>
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Schedule 1:
                            </strong>{' '}
                            An IRS-stamped{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Form 2290 Schedule 1
                            </strong>{' '}
                            is commonly required as proof of HVUT payment for
                            IRP registration.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              IRP Requirements:
                            </strong>{' '}
                            Vehicles generally qualify based on{' '}
                            <strong style={{ fontWeight: 600 }}>
                              weight, axle count, and operation across multiple
                              jurisdictions.
                            </strong>
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Recordkeeping:
                            </strong>{' '}
                            Keep accurate{' '}
                            <strong style={{ fontWeight: 600 }}>
                              mileage and trip records
                            </strong>{' '}
                            for each jurisdiction and renew your IRP
                            registration as required.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Compliance:
                            </strong>{' '}
                            Keep your{' '}
                            <strong style={{ fontWeight: 600 }}>
                              IRP credentials and Schedule 1
                            </strong>{' '}
                            up to date to avoid registration delays, penalties,
                            or operating issues.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      If you&apos;re operating heavy vehicles across multiple
                      states or provinces, you&apos;ve probably heard of the{' '}
                      <span style={{ fontWeight: 600 }}>
                        International Registration Plan (IRP).
                      </span>{' '}
                      However, how does it relate to the Heavy Highway Vehicle
                      Use Tax Return,{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Form 2290
                      </Link>
                      ? Both are essential for truckers, but they have different
                      functions and different filing requirements.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      In this guide, we’ll break down everything you need to
                      know about{' '}
                      <span style={{ fontWeight: 600 }}>
                        IRP and{' '}
                        <Link
                          href="/resources/excise-tax/form-2290-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          2290 tax form
                        </Link>
                        ,
                      </span>{' '}
                      how they work together, and how to stay compliant.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is an International Registration Plan (IRP)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must Register for IRP?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How does IRP work?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#documents" sx={{ color: '#0202ff' }}>
                          Documents Required for IRP Registration
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#happen" sx={{ color: '#0202ff' }}>
                          What happens after Registering?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#irp" sx={{ color: '#0202ff' }}>
                          IRP recordkeeping and Renewal
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#benefits" sx={{ color: '#0202ff' }}>
                          Benefits of IRP Registration
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          IRP vs Form 2290: What’s the Difference?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#work" sx={{ color: '#0202ff' }}>
                          How IRP and Form 2290 Work together?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Tips for Truckers
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is an International Registration Plan (IRP)?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The International Registration Plan (IRP) is an agreement
                  among the 48 contiguous U.S. states, the District of Columbia,
                  and several Canadian provinces that allows commercial vehicles
                  to operate across multiple jurisdictions under a single
                  registration.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  For truckers, this means:
                </Typography>
                <Box component="ul" sx={{ mb: 5, '& li': { mb: 1.5 } }}>
                  <li>You register in your base jurisdiction</li>
                  <li>You receive apportioned plates</li>
                  <li>
                    {' '}
                    Your registration fees are distributed based on mileage
                    driven in each state or province{' '}
                  </li>
                </Box>

                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Ready to Register Under IRP?
                      </Typography>
                      <Typography variant="body2">
                        Make sure you&apos;ve filed{' '}
                        <span style={{ fontWeight: 600 }}>IRS Form 2290</span>{' '}
                        first! It&apos;s a required step for IRP
                        registration.{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 250, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=irp`}
                      >
                        File Form 2290 Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must Register for IRP?
                </Typography>

                <Typography variant="body1">
                  You must register under IRP if you:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    Operate a{' '}
                    <span style={{ fontWeight: 600 }}>
                      commercial vehicle over 26,000 lbs
                    </span>{' '}
                    gross weight, or{' '}
                  </li>
                  <li>
                    Have{' '}
                    <span style={{ fontWeight: 600 }}>3 or more axles,</span>{' '}
                    regardless of weight, or
                  </li>
                  <li>
                    {' '}
                    Use your vehicle in{' '}
                    <span style={{ fontWeight: 600 }}>
                      two or more IRP jurisdictions
                    </span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  If you&apos;re an{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-filing-guide-for-owner-operators/"
                    sx={{ color: '#0202ff' }}
                  >
                    owner-operator,
                  </Link>{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-filing-guide-for-fleet-managers/"
                    sx={{ color: '#0202ff' }}
                  >
                    fleet manager,
                  </Link>{' '}
                  or cross-border hauler, IRP is essential.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How does IRP work?
                </Typography>

                <Typography variant="body1">
                  IRP simplifies registration for multistate operations:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    You apply in your{' '}
                    <span style={{ fontWeight: 600 }}>
                      base jurisdiction
                    </span>{' '}
                    (your home state){' '}
                  </li>
                  <li>
                    You declare the{' '}
                    <span style={{ fontWeight: 600 }}>jurisdictions</span> where
                    you&apos;ll operate{' '}
                  </li>
                  <li>
                    {' '}
                    You report{' '}
                    <span style={{ fontWeight: 600 }}>estimated miles</span> per
                    jurisdiction (for new accounts){' '}
                  </li>
                  <li>
                    {' '}
                    You receive an{' '}
                    <span style={{ fontWeight: 600 }}>
                      Apportioned Plate
                    </span>{' '}
                    and a <span style={{ fontWeight: 600 }}>Cab Card</span>{' '}
                    listing all approved jurisdictions{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  The plate is valid across all IRP member jurisdictions.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="documents"
                >
                  Documents Required for IRP Registration
                </Typography>

                <Typography variant="body1">
                  Before you apply for IRP registration, you’ll need to gather
                  several key documents to verify your eligibility and vehicle
                  details. Requirements may vary slightly by state, but
                  generally, you&apos;ll need:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Proof of vehicle ownership
                    </span>{' '}
                    (Title or lease agreement){' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Valid USDOT number and Motor Carrier number (if
                      applicable)
                    </span>{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Proof of residency or business location
                    </span>{' '}
                    (utility bill, business license, etc.){' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Vehicle information
                    </span>{' '}
                    (VIN, make, model, weight){' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Proof of Heavy Vehicle Use Tax (HVUT) payment
                    </span>{' '}
                    – IRS Form 2290 Schedule 1{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Proof of insurance
                    </span>{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Estimated mileage per jurisdiction
                    </span>{' '}
                    (used for apportioning fees){' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Tip:</b> Make sure all documents are up to date to avoid
                  delays in registration.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="happen"
                >
                  What Happens After Registration?
                </Typography>

                <Typography variant="body1">
                  After getting your IRP credentials:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    Place the{' '}
                    <span style={{ fontWeight: 600 }}>
                      Apportioned Plate
                    </span>{' '}
                    on your vehicle{' '}
                  </li>
                  <li>The Cab Card must be always kept inside the vehicle </li>
                  <li>
                    {' '}
                    Only operate in the jurisdictions listed on your card{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  If your travel plans change, you can file a{' '}
                  <span style={{ fontWeight: 600 }}>
                    supplemental application
                  </span>{' '}
                  to add jurisdictions
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="irp"
                >
                  IRP Recordkeeping and Renewals
                </Typography>

                <Typography variant="body1">
                  You must{' '}
                  <span style={{ fontWeight: 600 }}>
                    renew your IRP registration annually
                  </span>{' '}
                  and keep proper mileage records.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Required Records:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li> Trip sheets or ELD reports </li>
                  <li> Beginning and ending odometer readings </li>
                  <li> Dates of each trip </li>
                  <li> Routes taken </li>
                  <li> Total miles driven per jurisdiction </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  States may audit your records, so keep them{' '}
                  <span style={{ fontWeight: 600 }}>for at least 3 years.</span>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="benefits"
                >
                  Benefits of IRP Registration
                </Typography>
                <Box component="ul" sx={{ mb: 5, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Legal compliance
                    </span>{' '}
                    when operating across multiple jurisdictions{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Simplified registration
                    </span>{' '}
                    process with one plate and one renewal{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Fair fee structure
                    </span>{' '}
                    based on actual miles driven{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Reduced paperwork
                    </span>{' '}
                    compared to registering in each state{' '}
                  </li>
                  <li>
                    {' '}
                    Access to{' '}
                    <span style={{ fontWeight: 600 }}>
                      electronic portals
                    </span>{' '}
                    in many jurisdictions for faster processing{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 87,
                  }}
                  id="form"
                >
                  IRP vs. Form 2290: What’s the Difference?
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Features
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            IRP (International Registration Plan)
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 2290 (Heavy Vehicle Use Tax)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Purpose </TableCell>
                          <TableCell>
                            Registers commercial vehicles to operate across
                            multiple states/provinces
                          </TableCell>
                          <TableCell>
                            It is used to report and pay the federal highway use
                            tax
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Filed With </TableCell>
                          <TableCell>
                            State Department of Motor Vehicles (DMV){' '}
                          </TableCell>
                          <TableCell>Internal Revenue Service (IRS) </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Covers </TableCell>
                          <TableCell>
                            Apportioned vehicle registration and license fees
                          </TableCell>
                          <TableCell>
                            Federal excise tax for heavy highway vehicles
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Applies To </TableCell>
                          <TableCell>
                            Vehicles exceeding 26,000 lbs or having 3 or more
                            axles operating in multiple jurisdictions{' '}
                          </TableCell>
                          <TableCell>
                            Vehicles over 55,000 lbs gross weight{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Result </TableCell>
                          <TableCell>Apportioned Plate + Cab Card</TableCell>
                          <TableCell>
                            IRS Schedule 1 (Proof of Payment)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Filing Schedule </TableCell>
                          <TableCell>
                            Annually (or as required by state){' '}
                          </TableCell>
                          <TableCell>
                            Annually (tax period July 1 – June 30){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Are they related? </TableCell>
                          <TableCell>
                            Yes. While they are separate filings, Form 2290 is
                            required to complete IRP registration.
                          </TableCell>
                          <TableCell>
                            Yes. Though filed independently, proof of Form 2290
                            is a condition for IRP approval.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="work"
                >
                  How IRP and Form 2290 work Together?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  While IRP and Form 2290 are separate requirements, they are
                  closely connected in terms of vehicle compliance:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>IRP</span> covers{' '}
                    <span style={{ fontWeight: 600 }}>
                      vehicle registration
                    </span>{' '}
                    across multiple states and provinces.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 2290
                    </span> covers{' '}
                    <span style={{ fontWeight: 600 }}>federal tax</span>{' '}
                    obligations for heavy highway vehicles (over 55,000
                    lbs).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To{' '}
                  <span style={{ fontWeight: 600 }}>
                    register a vehicle under IRP
                  </span>
                  , you must first pay the HVUT and submit{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-schedule-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 2290 Schedule 1
                  </Link>{' '}
                  as proof. Without this document, your IRP application may be
                  delayed or rejected.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontSize: 24, marginRight: 8 }}>ðŸš›</span>{' '}
                  <Typography>
                    Think of Form 2290 as your vehicle’s federal tax ticket,
                    while IRP is your license to drive across jurisdictions.
                  </Typography>
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="key"
                >
                  Key Tips for Truckers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To avoid penalties, delays, or fines, keep these compliance
                  tips in mind:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      File Form 2290 annually
                    </span>{' '}
                    (usually by August 31**) and keep your stamped Schedule 1
                    handy.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Renew IRP registration on time
                    </span>
                    —each state has different renewal windows.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Track mileage</span> per
                    jurisdiction accurately using ELDs or logbooks.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Keep all documents
                    </span>{' '}
                    organized and ready for audits or roadside inspections.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Update your IRP account
                    </span>{' '}
                    when you add/remove vehicles or change business
                    addresses.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <Typography>
                    ** If the deadline falls on the weekend or federal holiday,
                    it is extended to the next business days.
                  </Typography>
                </Alert>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above information is referenced from the official
                        IRP organization. For more details, please refer to the{' '}
                        <Link
                          href="https://www.irponline.org/"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRP website.
                        </Link>{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file HVUT Form 2290 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File IRS Form 2290 easily with TaxZerone starting at just&nbsp;
              <b>$19.99/return</b>. You will only have to enter the required
              information, calculate and pay the balance tax due, transmit the
              return to the IRS, and get your stamped Schedule 1 in minutes!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              The whole filing process gets completed in less than 5 minutes!
              You will also get email updates on your return status as soon as
              the IRS processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file HVUT Form 2290 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
              >
                E-file Form 2290 Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get your stamped Schedule 1 in minutes!
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container> */}
      <Container maxWidth="lg" sx={{ mb: 8, mt: { xs: 6, sm: 6, md: 6 } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            textAlign: 'center',
          }}
        >
          Need Help Filing Form 2290 for IRP Compliance?
        </Typography>
        <Grid
          container
          spacing={2}
          m={0}
          mt={0}
          className="MuiGrid-root-width"
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Grid
            size={{ md: 6, xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'center',
                xs: 'center',
              },
            }}
          >
            <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
              <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                Before you can complete your IRP registration or renewal, you
                must file IRS Form 2290 and get your Schedule 1 as proof of
                payment.
              </Typography>

              <Box
                sx={{
                  width: 'auto',
                  textAlign: 'left',
                  alignItems: 'center',
                  display: 'inline-block',
                  mt: { xs: 2, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  className="btn-primary medium"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=irp`}
                  sx={{
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  E-file Now
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: 0, textAlign: 'center' }}
                  id="auto-magic-2290"
                >
                  Save time & Try it today!
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} textAlign="right">
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/QnUmGYD2yLw?&rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form2290Instructions;
